// src/userFlow/contactPage.js
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, HelpCircle, Send, CheckCircle, XCircle } from 'lucide-react';
import API_BASE from '../apiBase';

const CONTACTS = [
  { label: 'General', email: 'contact@sodasips.ca' },
  { label: 'Product Owner', email: 'sodaadmin@sodasips.ca' },
  { label: 'Development Lead', email: 'dev@sodasips.ca' },
];

const CATEGORIES = [
  'General enquiry',
  'Order / Shipping',
  'Product issue',
  'Technical issue',
  'Collaboration / Press',
  'Other',
];

const FAQ = [
  {
    q: 'How long does shipping take?',
    a: 'Orders typically ship within 24–72 hours. Transit times vary by region and carrier.',
  },
  {
    q: 'Do you offer sugar-free / vegan / gluten-free options?',
    a: 'Yes! Use the filters on the Home page to show sugar-free, vegan, gluten-free, and all-natural products.',
  },
  {
    q: 'Can I cancel or change my order after placing it?',
    a: 'If your order hasn’t shipped, contact us ASAP with your order number and we’ll do our best.',
  },
  {
    q: 'My package arrived damaged—what should I do?',
    a: 'Snap a few photos of the packaging and contents and send them along with your order number via this form.',
  },
  {
    q: 'Do you ship internationally?',
    a: 'We currently ship domestically; international options are being evaluated.',
  },
];

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [orderId, setOrderId] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot to deter bots:
  const [website, setWebsite] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [serverSuccess, setServerSuccess] = useState('');

  const canSubmit =
    name.trim() &&
    email.trim() &&
    message.trim() &&
    subject.trim() &&
    !submitting;

  async function onSubmit(e) {
  e.preventDefault();
  setServerError('');
  setServerSuccess('');

  // Honeypot
  if (website.trim()) {
    setServerError('Form could not be submitted. (bot check failed)');
    return;
  }

  if (!canSubmit) return;

  setSubmitting(true);
  try {
    const res = await fetch(`${API_BASE}/api/support/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        category,
        order_id: orderId.trim() || undefined,
        message: message.trim(),
      }),
    });

    let data = null;
    try { data = await res.json(); } catch { /* non-JSON error body */ }

    if (!res.ok) {
      throw new Error(data?.error || `${res.status} ${res.statusText}`);
    }

    setServerSuccess('Thanks! Your message has been sent. We’ll get back to you shortly.');
    // Reset form
    setName('');
    setEmail('');
    setSubject('');
    setCategory(CATEGORIES[0]);
    setOrderId('');
    setMessage('');
  } catch (err) {
    setServerError(err.message || 'Something went wrong. Please try again.');
  } finally {
    setSubmitting(false);
  }
}


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header (compact) */}
      <header className="bg-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
            SodaSips
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            <Link to="/" className="text-gray-700 hover:text-orange-500">Home</Link>
            <Link to="/about" className="text-gray-700 hover:text-orange-500">About</Link>
            <Link to="/contact" className="text-gray-900 font-semibold">Contact</Link>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-white to-red-100 opacity-60" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-sm font-semibold text-orange-600 tracking-wide uppercase">Contact</p>
                <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
                  We’re here to help—questions, issues, or collaborations.
                </h1>
                <p className="mt-4 text-lg text-gray-600">
                  Use the form to contact our team about orders, technical issues, or partnership ideas.
                  Prefer email? Reach out directly below.
                </p>

                {/* Quick Contact Emails */}
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {CONTACTS.map(c => (
                    <a
                      key={c.email}
                      href={`mailto:${c.email}`}
                      className="flex items-start gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition"
                    >
                      <div className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-orange-50 text-orange-600">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{c.label}</div>
                        <div className="text-sm text-gray-600">{c.email}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Form + FAQ */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-orange-600" />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Send us a message</h2>
                </div>

                {/* Status Messages */}
                {serverSuccess ? (
                  <div className="mt-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 text-green-800 px-3 py-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>{serverSuccess}</span>
                  </div>
                ) : null}
                {serverError ? (
                  <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2">
                    <XCircle className="w-4 h-4" />
                    <span>{serverError}</span>
                  </div>
                ) : null}

                <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
                  {/* Honeypot */}
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Your Name *</label>
                      <input
                        className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email *</label>
                      <input
                        type="email"
                        className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Category *</label>
                      <select
                        className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Order # (optional)</label>
                      <input
                        className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="e.g., SS-12345"
                        value={orderId}
                        onChange={(e) => setOrderId(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Subject *</label>
                    <input
                      className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Message *</label>
                    <textarea
                      rows={6}
                      className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      For technical issues, include any error messages and your browser/device info.
                    </p>
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white
                        ${canSubmit ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-300 cursor-not-allowed'}`}
                    >
                      <Send className="w-4 h-4" />
                      Send Message
                    </button>
                  </div>
                </form>

                {/* Fallback note */}
                <p className="mt-4 text-xs text-gray-500">
                  Having trouble with the form? Email us at{' '}
                  <a className="underline" href="mailto:contact@sodasips.ca">contact@sodasips.ca</a>.
                </p>
              </div>
            </div>

            {/* FAQ */}
            <aside>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-orange-600" />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">FAQ</h2>
                </div>

                <div className="mt-4 divide-y divide-gray-100">
                  {FAQ.map((item, i) => (
                    <details key={i} className="group py-3">
                      <summary className="flex cursor-pointer list-none items-center justify-between text-gray-900 font-medium">
                        <span>{item.q}</span>
                        <span className="ml-4 text-gray-400 group-open:rotate-180 transition-transform">⌄</span>
                      </summary>
                      <p className="mt-2 text-sm text-gray-600">{item.a}</p>
                    </details>
                  ))}
                </div>

                <div className="mt-6 text-sm text-gray-600">
                  Can’t find what you’re looking for?{' '}
                  <Link to="/returns" className="text-orange-600 hover:underline">See returns</Link>{' '}
                  or{' '}
                  <a href="mailto:contact@sodasips.ca" className="text-orange-600 hover:underline">email us</a>.
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      {/* Footer — same as HomePage */}
      <footer className="bg-gray-800 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-4">
                SodaSips
              </div>
              <p className="text-gray-400">
                Your one-stop shop for the world's best sodas and beverages. Fresh, fizzy, and delivered to your door.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-orange-400 transition-colors duration-200">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition-colors duration-200">Contact</Link></li>
                <li><Link to="/returns" className="hover:text-orange-400 transition-colors duration-200">Returns</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://facebook.com" className="hover:text-orange-400 transition-colors duration-200">Facebook</a></li>
                <li><a href="https://twitter.com" className="hover:text-orange-400 transition-colors duration-200">Twitter</a></li>
                <li><a href="https://instagram.com" className="hover:text-orange-400 transition-colors duration-200">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SodaSips. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
